import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle2, ShieldCheck, HeartHandshake } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import Breadcrumbs from "./Breadcrumbs";
import SEO from "./SEO";
import { biohazardClaims } from "../data/biohazardClaims";
import { businessInfo } from "../mock";
import { localBusinessSchema, faqSchema, breadcrumbSchema } from "../utils/schemas";

const PILLAR = "/biohazard-bodily-fluid-claims-florida";

const BiohazardClaimPage = () => {
  const { slug } = useParams();
  const page = biohazardClaims[slug];
  if (!page) return <Navigate to="/" replace />;

  const siblings = Object.values(biohazardClaims).filter(
    (p) => p.slug !== slug && !p.isPillar
  );
  const crumbTrail = page.isPillar
    ? [{ name: "Biohazard & Bodily Fluid Claims", path: PILLAR }]
    : [
        { name: "Biohazard & Bodily Fluid Claims", path: PILLAR },
        { name: page.topic, path: `/${page.slug}` },
      ];
  const schemas = [
    localBusinessSchema(),
    faqSchema(page.faqs),
    breadcrumbSchema([{ name: "Home", path: "/" }, ...crumbTrail]),
  ];

  return (
    <div className="bg-black min-h-screen text-white">
      <SEO
        title={page.metaTitle}
        description={page.metaDescription}
        path={`/${page.slug}`}
        keywords={page.keywords}
        schemas={schemas}
      />
      <Header />
      <Breadcrumbs items={crumbTrail} />

      {/* Hero */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-4 w-4 text-[#E11D26]" />
            <span className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
              Biohazard Claims · {page.topic}
            </span>
          </div>
          <h1
            className="text-white text-3xl sm:text-4xl md:text-5xl font-black uppercase leading-[1.02]"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            {page.h1}
          </h1>
          <p className="mt-6 text-white/80 text-base sm:text-lg leading-relaxed">
            {page.intro}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/#contact"
              data-testid="bio-cta-review"
              className="group inline-flex items-center justify-center gap-3 bg-[#E11D26] hover:bg-[#c11820] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <span className="text-sm">Free Confidential Review</span>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href={`tel:${businessInfo.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm">{businessInfo.phone}</span>
            </a>
          </div>
        </div>
      </section>

      {/* What's covered / what we document */}
      <section className="py-14 md:py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-black text-white uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            What We Document On Your Claim
          </h2>
          <ul className="mt-7 grid sm:grid-cols-2 gap-3">
            {page.points.map((p, i) => (
              <li key={i} className="flex items-start gap-3 bg-[#0F0F0F] border border-white/10 p-4">
                <CheckCircle2 className="h-5 w-5 text-[#E11D26] shrink-0 mt-0.5" />
                <span className="text-white/85 text-sm leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Process */}
      <section className="py-14 md:py-16 bg-black border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-black text-white uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            How We Handle Your Claim
          </h2>
          <div className="mt-9 grid md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {[
              { n: "01", t: "Confidential Review", d: "We review your policy and situation privately, at no cost, and tell you exactly what's covered." },
              { n: "02", t: "Document", d: "We document the certified remediation, structural damage, and contents in full detail." },
              { n: "03", t: "Build the Claim", d: "We prepare an independent, replacement-cost estimate that reflects the true scope of the work." },
              { n: "04", t: "Negotiate", d: "We file and negotiate with the carrier and escalate if the offer falls short — so you aren't underpaid." },
            ].map((s) => (
              <div key={s.n} className="bg-[#0F0F0F] p-6">
                <div className="text-[#E11D26] text-4xl font-black leading-none" style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}>
                  {s.n}
                </div>
                <h3 className="mt-4 text-white font-black uppercase text-sm tracking-wide" style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}>
                  {s.t}
                </h3>
                <p className="mt-3 text-white/70 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          {!page.isPillar && (
            <div className="mt-8">
              <Link
                to={PILLAR}
                data-testid="bio-pillar-link"
                className="inline-flex items-center gap-2 bg-[#E11D26]/10 border border-[#E11D26]/50 hover:bg-[#E11D26] text-white px-5 py-3 text-sm font-bold tracking-wider uppercase transition-colors"
              >
                <ShieldCheck className="h-4 w-4" /> All Biohazard &amp; Bodily Fluid Claims
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-black text-white uppercase" style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}>
            {page.topic} Claim FAQ
          </h2>
          <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
            {page.faqs.map((f, i) => (
              <details key={i} className="group py-5" data-testid={`bio-faq-${i}`}>
                <summary className="flex justify-between items-start cursor-pointer list-none gap-6">
                  <h3 className="text-white text-base sm:text-lg font-bold uppercase tracking-wide leading-snug">
                    {f.question}
                  </h3>
                  <span className="text-[#E11D26] text-xl group-open:rotate-45 transition-transform shrink-0">+</span>
                </summary>
                <p className="mt-4 text-white/75 leading-relaxed text-sm sm:text-base">{f.answer}</p>
              </details>
            ))}
          </div>
          <p className="mt-6 text-white/45 text-xs italic">
            This information is general and educational, not legal advice. Coverage,
            limits, and endorsements vary by policy and cause of loss; review your
            policy or consult a professional for your specific situation.
          </p>
        </div>
      </section>

      {/* Cluster nav */}
      <section className="py-14 bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-6">
            <HeartHandshake className="h-5 w-5 text-[#E11D26]" />
            <h2 className="text-2xl sm:text-3xl font-black text-white uppercase" style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}>
              {page.isPillar ? "Claims We Handle" : "Related Biohazard Claims"}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {siblings.map((s) => (
              <Link
                key={s.slug}
                to={`/${s.slug}`}
                data-testid={`bio-cluster-${s.slug}`}
                className="group flex items-center justify-between gap-4 bg-[#0F0F0F] border border-white/10 hover:border-[#E11D26] hover:bg-[#1a0709] p-5 transition-colors"
              >
                <span className="text-white font-bold text-sm uppercase tracking-wide">
                  {s.topic} Claims
                </span>
                <ArrowRight className="h-4 w-4 text-[#E11D26] transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 bg-[#E11D26]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-white text-2xl sm:text-3xl md:text-4xl font-black uppercase leading-tight" style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}>
              You handle the healing. We&apos;ll handle the claim.
            </h3>
            <p className="mt-2 text-white/90 text-sm sm:text-base">
              Free, confidential review — no upfront cost.
            </p>
          </div>
          <a
            href={`tel:${businessInfo.phoneRaw}`}
            className="inline-flex items-center justify-center gap-2 bg-black hover:bg-[#0A0A0A] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors shrink-0"
          >
            <Phone className="h-4 w-4" /> {businessInfo.phone}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BiohazardClaimPage;
